import { ExamLocationVerdict, type ExamLocationRequestApprovalType } from '@exam-center/graphql';
import { dayjs, type PropsWithChildren, zodResolver } from '@shared/utils';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import {
  examCenterLocationRequestVerdictFormSchema,
  type ExamCenterLocationRequestVerdictFormType,
} from './schema';

interface ExamCenterLocationRequestVerdictFormProviderProps extends PropsWithChildren {
  initialValues?: {
    verdicts: {
      verdict: ExamLocationVerdict | null;
      comment: string | null;
      approvalType: ExamLocationRequestApprovalType | null;
      grantedRoomId: string | null;
      submittedAt: Date | null;
    }[];
  };
}

export default function ExamCenterLocationRequestVerdictFormProvider(
  props: Readonly<ExamCenterLocationRequestVerdictFormProviderProps>,
) {
  const { children, initialValues } = props;

  const verdicts = useMemo(() => {
    return initialValues?.verdicts ?? [];
  }, [initialValues]);

  const { latestSubmittedVerdict, latestApprovedVerdict } = useMemo(() => {
    let latestSubmittedVerdict = null;
    let latestApprovedVerdict = null;

    if (verdicts.length === 0) {
      return { latestSubmittedVerdict: null, latestApprovedVerdict: null };
    }

    for (const verdict of verdicts) {
      if (!verdict.submittedAt) {
        continue;
      }
      if (
        !latestSubmittedVerdict ||
        dayjs(verdict.submittedAt).isAfter(dayjs(latestSubmittedVerdict.submittedAt))
      ) {
        latestSubmittedVerdict = verdict;
      }

      if (verdict.verdict === ExamLocationVerdict.Approved) {
        if (
          !latestApprovedVerdict ||
          dayjs(verdict.submittedAt).isAfter(dayjs(latestApprovedVerdict.submittedAt))
        ) {
          latestApprovedVerdict = verdict;
        }
      }
    }

    return { latestSubmittedVerdict, latestApprovedVerdict };
  }, [verdicts]);

  const approver1Verdict =
    latestSubmittedVerdict?.verdict === ExamLocationVerdict.Approved ? latestApprovedVerdict : null;

  const savedVerdict = verdicts.find((verdict) => verdict.submittedAt === null);
  const formContext = useForm<ExamCenterLocationRequestVerdictFormType>({
    resolver: zodResolver(examCenterLocationRequestVerdictFormSchema),
    defaultValues: {
      verdict: savedVerdict?.verdict ?? undefined,
      comment: savedVerdict?.comment ?? '',
      action: 'save',
      approvalType: savedVerdict?.approvalType ?? approver1Verdict?.approvalType ?? undefined,
      grantedRoomId: savedVerdict?.grantedRoomId ?? approver1Verdict?.grantedRoomId ?? '',
    },
  });

  return <FormProvider {...formContext}>{children}</FormProvider>;
}
